import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";

import { getGuestBookings } from "./services/bookingAPI";
import { fetchPropertySummaries } from "./services/propertySummaryService";

import dateFormatterDD_MM_YYYY from "../../utils/DateFormatterDD_MM_YYYY";
import { getBookingTimestamp } from "../../utils/getBookingTimestamp";
import { timestampToDate } from "../../utils/timestampToDate";
import { placeholderImage, normalizeImageUrl } from "./utils/image";
import { resolveAccommodationImageUrl } from "../../utils/accommodationImage";
import {
  getBookingId,
  getPaidBookings,
  getInquiryBookings,
  getPropertyId,
  normalizeGuestBookingsResponse,
  splitBookingsByTime,
  normalizeStayStatus,
  getInquiryStatusInfo,
} from "./utils/guestDashboardUtils";

const formatBookingDates = (bookingItem) => {
  const arrivalDate =
    timestampToDate(bookingItem?.arrivaldate ?? bookingItem?.arrival_date ?? bookingItem?.arrivalDate) || null;
  const departureDate =
    timestampToDate(bookingItem?.departuredate ?? bookingItem?.departure_date ?? bookingItem?.departureDate) || null;

  if (!arrivalDate || !departureDate) return "-";

  return `${dateFormatterDD_MM_YYYY(arrivalDate)} -> ${dateFormatterDD_MM_YYYY(departureDate)}`;
};

const BookingRow = ({ bookingItem, propertyMap, handleBookingClick }) => {
  const propertyId = getPropertyId(bookingItem);
  const fallbackTitle =
    bookingItem?.title ||
    bookingItem?.Title ||
    (bookingItem?.property_id ? `Property #${bookingItem.property_id}` : "Booking");
  const propertyInfo = propertyId ? propertyMap[propertyId] : undefined;
  const displayTitle = propertyInfo?.title || fallbackTitle;
  const imageUrl =
    propertyInfo?.imageUrl ||
    resolveAccommodationImageUrl(bookingItem?.images?.[0], "thumb") ||
    resolveAccommodationImageUrl(bookingItem?.property?.images?.[0], "thumb") ||
    normalizeImageUrl(bookingItem?.propertyImage || bookingItem?.image || bookingItem?.property?.coverImage || null);
  const bookingCity = propertyInfo?.city || bookingItem?.city || bookingItem?.location?.city || "Unknown city";
  const bookingStatus = String(bookingItem?.status || bookingItem?.Status || "");
  const bookingType = String(bookingItem?.bookingtype ?? "direct").toLowerCase();
  const inquiryStatusInfo = getInquiryStatusInfo(bookingStatus, bookingType);
  const hostName =
    propertyInfo?.hostName ||
    bookingItem?.hostName ||
    bookingItem?.host_name ||
    bookingItem?.host?.name ||
    bookingItem?.host?.fullName ||
    "";

  return (
    <button type="button" className="guest-card-row" onClick={() => handleBookingClick(bookingItem)}>
      <div className="guest-booking-row-inner">
        <div className="guest-booking-row-image">
          <img
            src={imageUrl}
            alt={displayTitle}
            onError={(e) => {
              e.currentTarget.src = placeholderImage;
            }}
          />
        </div>

        <div className="guest-booking-row-main">
          <div className="row-title">{displayTitle}</div>
          <div className="row-sub">{bookingCity}</div>

          {hostName && (
            <div className="row-host">
              Host: <span className="row-host-name">{hostName}</span>
            </div>
          )}

          <div className="guest-booking-row-meta">
            {inquiryStatusInfo ? (
              <>
                <span className={`guest-booking-status guest-booking-status--${inquiryStatusInfo.variant}`}>
                  {inquiryStatusInfo.label}
                </span>
                <span className="guest-booking-status-desc">{inquiryStatusInfo.description}</span>
              </>
            ) : (
              <span className="guest-booking-status">{normalizeStayStatus(bookingStatus) || "-"}</span>
            )}
            <span className="guest-booking-dates">{formatBookingDates(bookingItem)}</span>
          </div>
        </div>
      </div>
    </button>
  );
};

BookingRow.propTypes = {
  bookingItem: PropTypes.object.isRequired,
  propertyMap: PropTypes.object.isRequired,
  handleBookingClick: PropTypes.func.isRequired,
};

const BookingSection = ({ title, bookings, emptyMessage, propertyMap, handleBookingClick, extraClassName = "" }) => (
  <section className={`guest-card ${extraClassName}`.trim()}>
    <div className="guest-card-header">
      <span>{title}</span>
      <span className="guest-booking-badge">{bookings.length}</span>
    </div>
    <div className="guest-card-body">
      {bookings.length === 0 ? (
        <p className="guest-booking-empty">{emptyMessage}</p>
      ) : (
        bookings.map((bookingItem, index) => (
          <BookingRow
            key={bookingItem?.id || bookingItem?.ID || `${index}-${title}`}
            bookingItem={bookingItem}
            propertyMap={propertyMap}
            handleBookingClick={handleBookingClick}
          />
        ))
      )}
    </div>
  </section>
);

BookingSection.propTypes = {
  title: PropTypes.string.isRequired,
  bookings: PropTypes.array.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  propertyMap: PropTypes.object.isRequired,
  handleBookingClick: PropTypes.func.isRequired,
  extraClassName: PropTypes.string,
};

function GuestBooking() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [guestId, setGuestId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [propertyMap, setPropertyMap] = useState({});
  const [propLoading, setPropLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      try {
        const userInfo = await Auth.currentUserInfo();
        if (!isMounted) return;

        setGuestId(userInfo?.attributes?.sub || null);
      } catch {
        if (isMounted) setError("Could not load your session.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadUser();
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchBookings = useCallback(async () => {
    if (!guestId) return;

    setIsLoading(true);
    setError("");

    try {
      const bookingData = await getGuestBookings(guestId);
      const normalizedBookings = normalizeGuestBookingsResponse(bookingData);
      normalizedBookings.sort((a, b) => getBookingTimestamp(b) - getBookingTimestamp(a));
      setBookings(normalizedBookings);
    } catch {
      setError("Could not load your bookings.");
    } finally {
      setIsLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    if (guestId) fetchBookings();
  }, [guestId, fetchBookings]);

  const fetchPropertyDetails = useCallback(
    async (propertyIds) => {
      if (!propertyIds?.length) return;

      const toFetch = propertyIds.filter((id) => id && !propertyMap[id]);
      if (!toFetch.length) return;

      setPropLoading(true);
      try {
        const fetchedSummaries = await fetchPropertySummaries(toFetch);
        setPropertyMap((prev) => {
          const next = { ...prev };
          Object.entries(fetchedSummaries).forEach(([propertyId, summary]) => {
            next[propertyId] = summary;
          });
          return next;
        });
      } finally {
        setPropLoading(false);
      }
    },
    [propertyMap]
  );

  const cancelledBookings = useMemo(
    () => bookings.filter((b) => normalizeStayStatus(b?.status) === "Cancelled"),
    [bookings]
  );

  const paidBookings = useMemo(
    () => getPaidBookings(bookings).filter((b) => normalizeStayStatus(b?.status) !== "Cancelled"),
    [bookings]
  );

  const inquiryBookings = useMemo(() => getInquiryBookings(bookings), [bookings]);

  useEffect(() => {
    const combined = [...paidBookings, ...cancelledBookings];
    if (!combined.length) return;

    const ids = Array.from(new Set(combined.map((b) => getPropertyId(b)).filter(Boolean)));
    if (ids.length) fetchPropertyDetails(ids);
  }, [paidBookings, cancelledBookings, fetchPropertyDetails]);

  const handleBookingClick = (bookingItem) => {
    const bookingId = getBookingId(bookingItem);
    const propertyId = getPropertyId(bookingItem);

    if (bookingId) {
      navigate(`/guestdashboard/reservation/${encodeURIComponent(bookingId)}`);
      return;
    }
    if (propertyId) {
      navigate(`/listingdetails?ID=${encodeURIComponent(propertyId)}`);
    }
  };
  const TABS = [
  { key: "all", label: "All" },
  { key: "current", label: "Current" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
];

  const { currentBookings, upcomingBookings, pastBookings } = useMemo(
    () => splitBookingsByTime(paidBookings),
    [paidBookings]
  );
  const tabBookings = useMemo(() => {
  switch (activeTab) {
    case "current":
      return currentBookings;
    case "upcoming":
      return upcomingBookings;
    case "past":
      return pastBookings;
    default:
      return paidBookings;
  }
}, [activeTab, paidBookings, currentBookings, upcomingBookings, pastBookings]);

const tabEmptyMessages = {
  all: "You do not have any bookings yet.",
  current: "No current bookings this week.",
  upcoming: "You do not have any upcoming bookings yet.",
  past: "You do not have any past bookings yet.",
};
const filteredBookings = useMemo(() => {
  if (!searchQuery.trim()) return tabBookings;
   const query = searchQuery.trim().toLowerCase();
  return tabBookings.filter((b) => {
    const propertyId = getPropertyId(b);
    const propertyInfo = propertyId ? propertyMap[propertyId] : undefined;
    const city = (propertyInfo?.city || b?.city || b?.location?.city || "").toLowerCase();
    const title = (propertyInfo?.title || b?.title || b?.Title || "").toLowerCase();
    const bookingId = String(getBookingId(b) || "").toLowerCase();
    return city.includes(query) || title.includes(query) || bookingId.includes(query);
  });
}, [tabBookings, searchQuery, propertyMap]);

  let bookingContent;
  if (isLoading) {
    bookingContent = <div className="guest-booking-loader">Loading...</div>;
  } 
   else {
    bookingContent = (
      <div className="guest-booking-bookingContent">
            {error && (
      <div className="guest-booking-error" role="alert">
        {error}
      </div>
    )}
        {propLoading && <div className="guest-booking-loader-inline">Loading property details...</div>}

               {inquiryBookings.length > 0 && (
          <div className="guest-booking-summary-grid">
            <BookingSection
              title="Requests"
              bookings={inquiryBookings}
              emptyMessage="No requests."
              propertyMap={propertyMap}
              handleBookingClick={handleBookingClick}
            />
          </div>
        )}

        <div className="guest-booking-search">
          <input
            type="text"
            aria-label="Search bookings"
            placeholder="Search by city, property or booking id"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

                <div className="guest-booking-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`guest-booking-tab${activeTab === tab.key ? " guest-booking-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="guest-booking-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
                <div className="guest-booking-summary-grid">
          <BookingSection
            title={TABS.find((t) => t.key === activeTab)?.label || "Bookings"}
            bookings={filteredBookings}
            emptyMessage={tabEmptyMessages[activeTab]}
            propertyMap={propertyMap}
            handleBookingClick={handleBookingClick}
            extraClassName={activeTab === "past" ? "guest-card--past" : ""}
          />
        </div>
                  {cancelledBookings.length > 0 && (
          <div className="guest-booking-summary-grid">
            <BookingSection
              title="Cancelled Bookings"
              bookings={cancelledBookings}
              emptyMessage="You do not have any cancelled bookings yet."
              propertyMap={propertyMap}
              handleBookingClick={handleBookingClick}
              extraClassName="guest-card--cancelled"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="guest-dashboard-shell">
      <div className="guest-dashboard-page-body guest-booking-page-body">
        <h1 className="guest-booking-title">Reservations</h1>
<p className="guest-booking-subtitle">View and manage your bookings</p>

        <div className="guest-dashboard-dashboards">
          <div className="guest-dashboard-content guest-booking-dashboard-content">
            <div className="guest-dashboard-accomodation-side guest-booking-accomodation-side">
             

              {bookingContent}
            </div>

            <aside className="guest-dashboard-personalInfoContent guest-booking-right-empty" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuestBooking;
